import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { marker as _ } from '@jsverse/transloco-keys-manager/marker';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';
import { NotificationService } from '../../shared/services/notification.service';
import { authConfig } from '../oauth-config';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private oauthService = inject(OAuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private permissionsService = inject(NgxPermissionsService);
  private notificationService = inject(NotificationService);

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private isLoginInProgressSubject$ = new BehaviorSubject<boolean>(false);
  public isLoginInProgress$ = this.isLoginInProgressSubject$.asObservable();

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values) => values.every((b) => b)));

  constructor() {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage);

    // useful for debugging:
    this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
      } else {
        console.warn('OAuthEvent Object:', event);
      }
    });

    // THe following cross-tab communication of fresh access tokens works usually in practice,
    // but if you need more robust handling the community has come up with ways to extend logic
    // in the library which may give you better mileage.
    //
    // See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    //
    // Until then we'll stick to this:
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

      if (!this.oauthService.hasValidAccessToken()) {
        this.router.navigateByUrl('/');
      }
    });

    this.oauthService.events.subscribe(() => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.oauthService.events
      .pipe(filter((e) => ['token_received'].includes(e.type)))
      .subscribe(() => this.oauthService.loadUserProfile());

    this.oauthService.events.pipe(filter((e) => ['token_expires'].includes(e.type))).subscribe(() => {
      this.oauthService.refreshToken();
    });

    this.oauthService.events.pipe(filter((e) => ['token_refresh_error'].includes(e.type))).subscribe(() => {
      this.logout();
    });

    this.oauthService.events.pipe(filter((e) => ['user_profile_loaded'].includes(e.type))).subscribe(() => {
      const permissions = this.identityClaims['permissions'];
      if (permissions) {
        const roles = this.identityClaims['roles'];
        if (roles) {
          permissions.push(...roles.map((role: string) => role.replace('ROLE_', 'role:').toLowerCase()));
        }

        this.permissionsService.loadPermissions(permissions);
      }
    });

    this.oauthService.events.pipe(filter((e) => ['logout'].includes(e.type))).subscribe(() => {
      this.permissionsService.flushPermissions();
    });
  }

  /**
   * @see https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/blob/master/src/app/core/auth.service.ts
   */
  public runInitialLoginSequence(): Promise<void> {
    return (
      new Promise<void>((resolve) => resolve())

        // For demo purposes, we pretend the previous call was very slow
        .then(() => new Promise<void>((resolve) => setTimeout(() => resolve(), 800)))

        .then(() => {
          // TODO: check token expiration?
          if (this.oauthService.hasValidAccessToken()) {
            return this.oauthService
              .loadUserProfile()
              .then(() => Promise.resolve())
              .catch(() => Promise.reject());
          }

          if (this.oauthService.getRefreshToken()) {
            return this.oauthService
              .refreshToken()
              .then(() => Promise.resolve())
              .catch(() => Promise.reject());
          }

          return Promise.resolve();
        })

        .then(() => {
          this.isDoneLoadingSubject$.next(true);

          // Check for the strings 'undefined' and 'null' just to be sure. Our current
          // login(...) should never have this, but in case someone ever calls
          // initImplicitFlow(undefined | null) this could happen.
          if (
            this.oauthService.state &&
            this.oauthService.state !== 'undefined' &&
            this.oauthService.state !== 'null'
          ) {
            let stateUrl = this.oauthService.state;
            if (stateUrl.startsWith('/') === false) {
              stateUrl = decodeURIComponent(stateUrl);
            }
            console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
            this.router.navigateByUrl(stateUrl);
          }
        })
        .catch(() => {
          this.oauthService.logOut();
          return this.isDoneLoadingSubject$.next(true);
        })
    );
  }

  login(email: string, password: string) {
    this.isLoginInProgressSubject$.next(true);

    this.oauthService
      .fetchTokenUsingPasswordFlow(email, password)
      .then(
        () => {
          const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
          this.router.navigateByUrl(returnUrl);
        },
        () => {
          this.notificationService.showError(_('auth.loginFailed'));
        },
      )
      .finally(() => this.isLoginInProgressSubject$.next(false));
  }

  logout() {
    this.oauthService.logOut();
    this.router.navigateByUrl('/login');
  }

  refresh() {
    this.oauthService.refreshToken();
  }

  loadProfile() {
    this.oauthService.loadUserProfile();
  }

  hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  public get accessToken() {
    return this.oauthService.getAccessToken();
  }
  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }
  public get idToken() {
    return this.oauthService.getIdToken();
  }
  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }
}
